import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Button, RadioButton } from '@factorialco/deprecated-design-system'
import { Spinner } from '@factorialco/factorial-one-react/dist/experimental'
import {
  TrainingsSessionDeleteEventDocument,
  TrainingsSessionUpdateStrategyEnum,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useDestroySession } from 'modules/trainings/hooks/Sessions/useDestroySession'

const DeleteEvent = () => {
  const { destroySession } = useDestroySession()
  const { id, classId, eventId } = useParams()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { data, loading } = useQuery(TrainingsSessionDeleteEventDocument, {
    variables: { ids: [Number(eventId)] },
  })
  const [strategy, setStrategy] = useState<TrainingsSessionUpdateStrategyEnum>('individually')

  if (loading) return <Spinner />

  const currentSession = data?.trainings.sessionsConnection.nodes.find(
    (s) => s.id === Number(eventId)
  )

  const cancel = () => {
    navigate(
      navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.fullPath({
        id: Number(id),
        classId: Number(classId),
      })
    )
  }

  const onSubmit = async () => {
    if (currentSession?.recurrent && !strategy) return

    const response = await destroySession({ id: Number(eventId), updateStrategy: strategy })
    const handled = handleResult(response, ({ messages }) => {
      if (!messages) {
        messages = i18n.t('trainings.trainings_revamp.delete_event_modal.error_delete')
      }
      app.setFlash({
        type: 'wrong',
        message: messages,
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('trainings.trainings_revamp.delete_event_modal.success_delete'),
      })
      cancel()
    }
  }

  if (!currentSession) {
    cancel()
    return
  }

  return (
    <Modal
      title={i18n.t('trainings.trainings_revamp.delete_event_modal.title')}
      description={i18n.t('trainings.trainings_revamp.delete_event_modal.description')}
      onSubmit={onSubmit}
      onClose={() => cancel()}
      illustration="delete"
      footer={
        <>
          <Button type="secondary" onClick={cancel}>
            {i18n.t('trainings.trainings_revamp.delete_event_modal.cancel')}
          </Button>

          <Button type="negative" submit>
            {i18n.t('trainings.trainings_revamp.delete_event_modal.submit')}
          </Button>
        </>
      }
    >
      {currentSession.recurrent && (
        <Box gap="s16">
          <RadioButton
            label={i18n.t(
              'trainings.trainings_revamp.sessions.edit_session_modal.session_strategy_modal.individually'
            )}
            onChange={() => {
              setStrategy('individually')
            }}
            value="individually"
            checked={strategy === 'individually'}
          />
          <RadioButton
            label={i18n.t(
              'trainings.trainings_revamp.sessions.edit_session_modal.session_strategy_modal.future'
            )}
            onChange={() => {
              setStrategy('future')
            }}
            value="future"
            checked={strategy === 'future'}
          />
          <RadioButton
            label={i18n.t(
              'trainings.trainings_revamp.sessions.edit_session_modal.session_strategy_modal.all'
            )}
            onChange={() => {
              setStrategy('all')
            }}
            value="all"
            checked={strategy === 'all'}
          />
        </Box>
      )}
    </Modal>
  )
}

export default DeleteEvent
